import { GoogleOutlined } from '@ant-design/icons';
import type { AuthMeResponse } from '@cult/schemas';
import type { QueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Alert, Avatar, Button, Card, Flex, Space, Typography } from 'antd';
import { SupportWidget } from '../components';
import { authKeys, loadAuthMe } from '../features/auth';
import { useOrgConfig } from '../state/use-org-config';

const { Title, Text } = Typography;

const ERROR_MESSAGES: Record<string, string> = {
  unauthorized: 'Access denied. Contact your administrator to request access.',
  email_not_verified: 'Please verify your email in Google before signing in.',
  oauth_error: 'Sign-in failed. Please try again.',
};

const LoginPage = () => {
  const { config } = useOrgConfig();
  const search = Route.useSearch();
  const errorMessage = search.error ? (ERROR_MESSAGES[search.error] ?? ERROR_MESSAGES.oauth_error) : null;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--ant-color-bg-layout, #f5f5f5)',
        padding: 24,
      }}
    >
      <Card style={{ width: '100%', maxWidth: 440, animation: 'scale-in var(--dur-slow) var(--ease-out) both' }}>
        <Flex vertical gap="middle">
          <Space align="center">
            {config.iconUrl ? <Avatar shape="square" src={config.iconUrl} alt={config.orgName} size={40} /> : null}
            <div>
              <Title level={3} style={{ margin: 0 }}>
                {config.orgName}
              </Title>
              <Text type="secondary">Sign in to continue</Text>
            </div>
          </Space>

          {errorMessage && <Alert type="error" showIcon message={errorMessage} />}

          {/* Direct browser redirect — required for OAuth cookie flow */}
          <Button block icon={<GoogleOutlined />} size="large" href="/api/auth/google">
            Continue with Google
          </Button>
        </Flex>
      </Card>

      {/* Signed-out visitors have no role yet, so only the HR channel applies. */}
      <SupportWidget showTechSupport={false} />
    </div>
  );
};

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): { error?: string } => ({
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
  // A signed-in visitor has nothing to do here. Sign-in errors always render, and a failed session
  // check (API down) leaves the page up — the Google button is still the way back in.
  beforeLoad: async ({ context, search }: { context: { queryClient: QueryClient }; search: { error?: string } }) => {
    if (search.error) return;
    const me = await context.queryClient
      .ensureQueryData<AuthMeResponse | null>({ queryKey: authKeys.me(), queryFn: loadAuthMe, staleTime: 60_000 })
      .catch(() => null);
    if (me) throw redirect({ to: '/home' });
  },
  component: LoginPage,
});
